import { useCallback, useEffect, useRef, useState } from "react";
import apiClient from "../../utils/apiClient";

/**
 * The institute's WhatsApp connection, read once for the whole dashboard.
 *
 * The dashboard needs it before it can choose what to show — the inbox, or the
 * setup guide — and the Config tab needs the same answer, so it is owned here
 * rather than fetched by each.
 *
 *   status   the `/whatsapp/connect/status` payload, or null until it arrives
 *   loading  true only for a visible (non-silent) load
 *   error    the last visible load's failure. Callers FAIL OPEN on it: a
 *            status check that could not be made is not evidence that
 *            WhatsApp is disconnected, and treating it as such would hide a
 *            working inbox behind a setup screen.
 *   refresh({ silent })  re-read; resolves to the new status, or null on failure.
 *            A silent refresh never shows a loader and never replaces a good
 *            answer with an error.
 *
 * `canManage` is absent from a backend older than this hook. Only a
 * config-holder could read the endpoint then, so absent means true.
 */
const useWhatsAppConnection = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const { data } = await apiClient.get("/whatsapp/connect/status");
      if (mounted.current) {
        setStatus(data);
        setError(null);
      }
      return data;
    } catch (err) {
      if (mounted.current && !silent) setError(err);
      return null;
    } finally {
      if (mounted.current && !silent) setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const connected = status ? !!status.connected : null;
  const canManage = status ? status.canManage !== false : false;

  return { status, setStatus, loading, error, refresh, connected, canManage };
};

export default useWhatsAppConnection;
